import { createFileRoute, Link } from "@tanstack/react-router";
import { PageHero } from "@/components/site/PageHero";
import {
  Search, Megaphone, PenTool, BarChart3, Code2, ShoppingBag,
  Share2, Mail, Video, ArrowRight,
} from "lucide-react";

export const Route = createFileRoute("/services")({
  head: () => ({
    meta: [
      { title: "Services — SEO, PPC, Social, Content & Web | BigBrandBooster" },
      { name: "description", content: "Full-funnel digital marketing services: SEO, paid media, social, content, analytics, web development and ecommerce growth." },
      { property: "og:title", content: "Services — BigBrandBooster" },
      { property: "og:description", content: "Full-funnel digital marketing services that drive measurable growth." },
    ],
    links: [{ rel: "canonical", href: "/services" }],
  }),
  component: ServicesPage,
});

const services = [
  { icon: Search, title: "SEO", desc: "Technical SEO, content systems and digital PR to dominate the SERP.", points: ["Technical audits", "Topical authority", "Local & international SEO"] },
  { icon: Megaphone, title: "Paid Media", desc: "Search, social and programmatic engineered for ROAS at every funnel stage.", points: ["Google & Bing Ads", "Meta, LinkedIn, TikTok", "Programmatic & CTV"] },
  { icon: Share2, title: "Social Media", desc: "Community-led content and creators that grow brand and demand.", points: ["Always-on content", "Creator partnerships", "Community management"] },
  { icon: PenTool, title: "Content & Creative", desc: "Brand systems, design, video and copy that performs.", points: ["Brand identity", "Editorial content", "Performance creative"] },
  { icon: Code2, title: "Web & Product", desc: "High-converting sites, landing pages and conversion experiences.", points: ["Headless builds", "CRO programs", "Landing page systems"] },
  { icon: ShoppingBag, title: "Ecommerce", desc: "D2C, marketplace and retention programs that scale revenue.", points: ["Shopify & custom", "Marketplaces", "Retention & CRM"] },
  // { icon: BarChart3, title: "Analytics & CRO", desc: "Attribution, dashboards and experimentation that compound returns.", points: ["GA4 & Looker", "Server-side tracking", "Experimentation"] },
  // { icon: Mail, title: "Email & CRM", desc: "Lifecycle journeys that turn buyers into advocates.", points: ["Klaviyo & HubSpot", "Lifecycle design", "Automation"] },
   { icon: Video, title: "Video & Motion", desc: "Story-driven video built for TikTok to TV.", points: ["Performance video", "Brand films", "Motion design"] },
];

function ServicesPage() {
  return (
    <>
      <PageHero
        eyebrow="Services"
        gradientClass="bg-services-gradient"
        title={<>End-to-end services for <span className="text-accent">full-funnel growth</span>.</>}
        description="Composable capabilities, senior-led delivery. Engage us for a single program or scale us into your full marketing function."
      />

      <section className="container-page py-24">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((s) => (
            <div key={s.title} className="group rounded-2xl border border-border bg-card p-7 hover:shadow-elegant transition">
              <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-primary-gradient text-primary-foreground shadow-soft">
                <s.icon className="h-5 w-5" />
              </span>
              <h3 className="mt-5 font-display text-xl font-semibold text-ink">{s.title}</h3>
              <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{s.desc}</p>
              <ul className="mt-5 space-y-2 text-sm">
                {s.points.map((p) => (
                  <li key={p} className="flex items-center gap-2 text-foreground/80">
                    <span className="h-1.5 w-1.5 rounded-full bg-primary" /> {p}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-16 flex flex-col md:flex-row items-center justify-between gap-6 rounded-3xl bg-ink text-background p-10">
          <div>
            <h3 className="font-display text-2xl md:text-3xl font-bold">Not sure where to start?</h3>
            <p className="mt-2 text-background/70">Book a free 30-minute strategy session with a senior strategist.</p>
          </div>
          <Link to="/contact" className="inline-flex items-center gap-2 rounded-full bg-primary-gradient px-6 py-3.5 text-sm font-semibold text-primary-foreground shadow-elegant">
            Book a session <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </>
  );
}
